import { Controller } from "@hotwired/stimulus"

/**
 * Manages the mobile navigation menu overlay and panel.
 * Handles animations, focus trapping for accessibility, and Escape key closing.
 */
export default class extends Controller {
  static targets = ["overlay", "panel", "closeButton", "firstFocusable"]

  connect() {
    this.isOpen = false
    this.previousFocusedElement = null
    this.boundOnKeydown = this.onKeydown.bind(this)

    this.syncClosedState()
  }

  disconnect() {
    this.close({ restoreFocus: false })
  }

  /**
   * Opens the mobile menu.
   * Disables body scroll, updates ARIA attributes, and traps focus.
   */
  open(event) {
    event.preventDefault()

    if (this.isOpen) {
      return
    }

    this.previousFocusedElement = document.activeElement
    this.isOpen = true
    document.body.classList.add("overflow-hidden")

    this.overlayTarget.setAttribute("aria-hidden", "false")
    this.overlayTarget.removeAttribute("inert")
    this.overlayTarget.classList.remove("pointer-events-none", "opacity-0")
    this.overlayTarget.classList.add("pointer-events-auto", "opacity-100")

    this.panelTarget.classList.remove("opacity-0", "-translate-y-4", "scale-[0.99]")
    this.panelTarget.classList.add("opacity-100", "translate-y-0", "scale-100")

    document.addEventListener("keydown", this.boundOnKeydown)

    requestAnimationFrame(() => {
      this.focusInitialElement()
    })
  }

  /**
   * Closes the mobile menu.
   * Restores body scroll and optionally returns focus to the trigger element.
   */
  close({ restoreFocus = true } = {}) {
    if (!this.isOpen && this.overlayTarget.getAttribute("aria-hidden") === "true") {
      document.body.classList.remove("overflow-hidden")
      return
    }

    this.isOpen = false
    document.body.classList.remove("overflow-hidden")

    this.overlayTarget.setAttribute("aria-hidden", "true")
    this.overlayTarget.setAttribute("inert", "")
    this.overlayTarget.classList.add("pointer-events-none", "opacity-0")
    this.overlayTarget.classList.remove("pointer-events-auto", "opacity-100")

    this.panelTarget.classList.add("opacity-0", "-translate-y-4", "scale-[0.99]")
    this.panelTarget.classList.remove("opacity-100", "translate-y-0", "scale-100")

    document.removeEventListener("keydown", this.boundOnKeydown)

    if (restoreFocus) {
      this.previousFocusedElement?.focus?.()
    }
  }

  /**
   * Closes the menu when clicking on the backdrop.
   */
  backdropClick(event) {
    event.preventDefault()
    this.close()
  }

  /**
   * Closes the menu when a link inside is clicked (useful for anchor navigation).
   */
  closeOnSelect() {
    this.close()
  }

  /**
   * Global keydown handler for Escape (close) and Tab (focus trap).
   */
  onKeydown(event) {
    if (!this.isOpen) {
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      return
    }

    if (event.key !== "Tab") {
      return
    }

    const focusables = this.focusableElements

    if (focusables.length === 0) {
      event.preventDefault()
      return
    }

    const first = focusables[0]
    const last = focusables[focusables.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  /**
   * Ensures the menu is visually and functionally hidden on connection.
   */
  syncClosedState() {
    this.overlayTarget.setAttribute("aria-hidden", "true")
    this.overlayTarget.setAttribute("inert", "")
    this.overlayTarget.classList.add("pointer-events-none", "opacity-0")
    this.overlayTarget.classList.remove("pointer-events-auto", "opacity-100")

    this.panelTarget.classList.add("opacity-0", "-translate-y-4", "scale-[0.99]")
    this.panelTarget.classList.remove("opacity-100", "translate-y-0", "scale-100")
  }

  /**
   * Moves focus to the close button when the menu opens.
   */
  focusInitialElement() {
    this.closeButtonTarget.focus()
  }

  /**
   * Returns a list of all focusable elements within the menu.
   */
  get focusableElements() {
    return Array.from(
      this.overlayTarget.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hidden)
  }
}
